import { useOperationStatusChecker } from '@/features/invoices/components/Details/Components/Context/OperationStatus';
import { useIsView } from '@/features/invoices/components/Details/Components/Context/ViewEdit';
import { useGetFlags } from '@/hooks/useFeatureFlag';
import { useTranslation } from '@/i18n';
import {
  Tooltip as BaseTooltip,
  ButtonV2,
  ButtonV2Size,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import {
  FC,
  PropsWithChildren,
  memo,
  useCallback,
  useMemo,
  useState,
} from 'react';
import styles from './index.module.scss';

const cx = classnames.bind(styles);

export type Props = {
  onClick: () => Promise<void>;
  size: ButtonV2Size;
  isDisabled?: boolean;
  isScannerRequirementSatisfied?: boolean;
};

export const CreateInvoiceReportButton: FC<Props> = memo(
  ({
    onClick: _onClick,
    size,
    isDisabled: _isDisabled,
    isScannerRequirementSatisfied,
  }) => {
    const { t } = useTranslation();
    const { isView } = useIsView();
    const flagEdocMetadata = useGetFlags('E_DOC_METADATA');

    const operationChecker = useOperationStatusChecker();
    const [isLoading, setLoading] = useState(false);
    const onClick = useCallback(async () => {
      setLoading(true);
      try {
        await _onClick();
      } finally {
        setLoading(false);
      }
    }, [_onClick]);
    const isDisabled = useMemo(() => {
      return (
        _isDisabled ||
        !isView ||
        operationChecker.isEdit ||
        operationChecker.isOcrProcessing ||
        (!isScannerRequirementSatisfied && flagEdocMetadata)
      );
    }, [
      _isDisabled,
      isView,
      operationChecker.isEdit,
      operationChecker.isOcrProcessing,
      isScannerRequirementSatisfied,
      flagEdocMetadata,
    ]);
    const Tooltip: FC<PropsWithChildren> = ({ children }) => {
      const message = useMemo(() => {
        if (!operationChecker.isNotStated) {
          return (
            <div className={cx(styles['container'])}>
              <div>{t('invoice_create_report_message')}</div>
            </div>
          );
        } else if (!isScannerRequirementSatisfied && flagEdocMetadata) {
          return (
            <div className={cx(styles['container'])}>
              <div>{t('invoice_create_report_edoc_message')}</div>
            </div>
          );
        }
        return null;
      }, []);
      return isDisabled &&
        (!operationChecker.isNotStated ||
          (!isScannerRequirementSatisfied && flagEdocMetadata)) ? (
        <BaseTooltip
          overlayClassName={cx(styles['tooltipContainer'])}
          title={message}
          placement='topRight'
          wrapSpanTag
        >
          {children}
        </BaseTooltip>
      ) : (
        <>{children}</>
      );
    };
    return (
      <>
        {!(
          operationChecker.isApplied ||
          operationChecker.isArchived ||
          operationChecker.isOcrProcessing
        ) && (
          <Tooltip>
            <ButtonV2
              size={size}
              isDisabled={isDisabled}
              isLoading={isLoading}
              onClick={onClick}
            >
              {t('支払依頼を作成')}
            </ButtonV2>
          </Tooltip>
        )}
      </>
    );
  }
);
CreateInvoiceReportButton.displayName = 'CreateInvoiceReportButton';
